import clsx from 'clsx';
import { Fragment, useRef, useState } from 'react';

import PlayerDataRow from '@/modules/league/details/standings/PlayerDataRow';
import PlayerNameRow from '@/modules/league/details/standings/PlayerNameRow';
import TotalsDataRow from '@/modules/league/details/standings/TotalsDataRow';
import { shouldRenderColumn } from '@/modules/league/details/standings/utils';
import {
  PlayersOnStandings,
  SessionLeagueDetails,
  StandingsColumn,
} from '@/modules/league/new-components/types';
import { LeagueSessionUpDownTotalsData } from '@/modules/league/new-components/types';

interface StandingsTableProps {
  session: SessionLeagueDetails;
  players: PlayersOnStandings[];
  totals?: LeagueSessionUpDownTotalsData[];
  columns: StandingsColumn[];
}

export default function StandingsTable({ session, players, totals, columns }: StandingsTableProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = x - startX;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    if (!scrollContainerRef.current) return;

    setIsDragging(false);
  };

  const getRowBgClass = (index: number) => {
    const bgClasses = [
      'bg-moss-300',
      'mt-1 bg-moss-200',
      'mt-1 bg-moss-100',
      'mt-1 bg-moss-50',
      'mt-1 bg-moss-25',
      'mt-1 bg-gray-50',
    ];
    return index < bgClasses.length ? bgClasses[index] : 'mt-1 bg-gray-50';
  };

  return (
    <div
      className='mt-2 mb-4 select-text overflow-auto md:ml-4 rounded-lg border'
      ref={scrollContainerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className='vertical-header-cell inline-flex h-28 w-auto flex-nowrap bg-white font-semibold text-gray-900 lg:flex '>
        {columns.map((col, idx) => {
          return shouldRenderColumn(col, session) ? (
            <div
              key={`header-${idx}`}
              className={clsx(
                'inline-flex shrink-0  items-center px-2 py-3 text-sm font-medium [transform:rotate(180deg)] [writing-mode:vertical-rl]',
                col.width
              )}
            >
              {col.label}
            </div>
          ) : null;
        })}
      </div>
      <div className='inline-flex w-auto flex-col flex-nowrap border'>
        {players?.map((player, i) => {
          const matchingTotal = totals?.find(
            (total) => total.SessionTeamID === player.SessionTeamID
          );
          return (
            <Fragment key={`player-row-${i}`}>
              <div className={clsx('flex w-full border-y border-gray-300', getRowBgClass(i))}>
                <div className='inline-flex w-12 shrink-0 justify-center break-words border-r px-2 py-4 text-sm font-semibold'>
                  <span className='self-center'>
                    {columns[0]?.primaryDataKey ? player[columns[0].primaryDataKey] : ''}
                  </span>
                </div>
                <PlayerNameRow session={session} player={player} />
                {columns.map((column, idx) => {
                  const isFirstTwoColumns = idx === 0 || idx === 1;

                  return !isFirstTwoColumns && shouldRenderColumn(column, session) ? (
                    <PlayerDataRow
                      key={`player-row-${i}-${idx}`}
                      session={session}
                      player={player}
                      column={column}
                    />
                  ) : null;
                })}
              </div>
              {totals ? (
                <div
                  className={clsx(`flex w-full border-y border-gray-300 mt-1`, getRowBgClass(i))}
                >
                  <TotalsDataRow
                    totals={totals}
                    player={player}
                    columns={columns}
                    matchingTotal={matchingTotal}
                  />
                </div>
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
